import { useMemo, useState } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, FormField, QueryStateBoundary } from "@/components/ui";
import { useResources } from "@/api/hooks/useBookings";
import { useStudioOccupancy } from "@/api/hooks/useMemberships";
import { formatTimeRange } from "./hourMath";
import { MembersNav } from "./MembersNav";
import { BookSessionDialog } from "./BookSessionDialog";
import { SellDayPassDialog } from "./SellDayPassDialog";
import { SellPassPackDialog } from "./SellPassPackDialog";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Studio day grid — seat occupancy per 2-hour window for a chosen date,
 * plus the entry points for booking a session and selling a day pass /
 * pass pack. Resource is always "The Wilder Studio" (kind=studio),
 * resolved from the existing resources list (src/api/hooks/useBookings.ts
 * — no duplicate resource-listing endpoint needed).
 */
export function StudioPage() {
  const { data: resources, isLoading: resourcesLoading, error: resourcesError } = useResources();
  const studioResource = useMemo(() => resources?.find((r) => r.kind === "studio"), [resources]);

  const [sessionDate, setSessionDate] = useState(todayIso());
  const [bookOpen, setBookOpen] = useState(false);
  const [dayPassOpen, setDayPassOpen] = useState(false);
  const [passPackOpen, setPassPackOpen] = useState(false);

  const { data: occupancy, isLoading, error, refetch } = useStudioOccupancy(studioResource?.id, sessionDate);

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Studio day grid</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDayPassOpen(true)} data-testid="sell-day-pass-button">
            Sell day pass
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setPassPackOpen(true)} data-testid="sell-pass-pack-button">
            Sell pass pack
          </Button>
          <Button size="sm" onClick={() => setBookOpen(true)} disabled={!studioResource} data-testid="book-session-button">
            Book session
          </Button>
        </div>
      </div>

      <MembersNav />

      <QueryStateBoundary isLoading={resourcesLoading} error={resourcesError}>
        {!studioResource ? (
          <EmptyState
            title="The Wilder Studio isn't seeded"
            description="Run scripts/seed_membership.py to seed the studio resource + membership tiers."
          />
        ) : (
          <div className="space-y-4">
            <FormField
              label="Date"
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              wrapperClassName="max-w-xs"
            />

            <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="studio-occupancy-grid">
                {(occupancy?.windows ?? []).map((w) => {
                  const full = w.seats_free <= 0;
                  const pct = w.capacity > 0 ? Math.round((w.booked / w.capacity) * 100) : 0;
                  return (
                    <Card key={w.window_index} className={full ? "border-danger/40" : undefined}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">{formatTimeRange(w.start_time, w.end_time)}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className={`text-lg font-semibold ${full ? "text-danger" : "text-text-primary"}`}>
                          {w.booked}/{w.capacity}
                        </p>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-bg-hover">
                          <div
                            className={`h-full rounded-full ${full ? "bg-danger" : "bg-brass"}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <p className="mt-1 text-xs text-text-muted">{w.seats_free} seats free</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </QueryStateBoundary>
          </div>
        )}
      </QueryStateBoundary>

      {studioResource && (
        <BookSessionDialog
          open={bookOpen}
          onClose={() => setBookOpen(false)}
          resourceId={studioResource.id}
          defaultDate={sessionDate}
        />
      )}
      <SellDayPassDialog open={dayPassOpen} onClose={() => setDayPassOpen(false)} defaultDate={sessionDate} />
      <SellPassPackDialog open={passPackOpen} onClose={() => setPassPackOpen(false)} />
    </div>
  );
}
